import { React, useEffect, useState, useMemo, useCallback } from 'react';
import { Row, Col, CardGroup } from 'react-bootstrap';
import '../DocList/DocCard.scss';
import { useSelector } from 'react-redux';
import { selectAuthProfiles } from '../../redux/selectors/authSelectors';
import { advanceMultiSearch } from '../../services';
import DocListSkeleton from '../DocList/DocListSkeleton';
import AlertMessage from '../AlertMessage';
import ReactPaginate from 'react-paginate';
import DocCard from '../DocList/DocCard';
import { AdvanceSearchPageEmptyState } from '../../pages/AdvanceSearchPage';
import { mitreDocTypes } from '../../constants/constants';
import { buildSearchCriteria } from '../../utils/filters/buildSearchCriteria';

const AdvanceSearchResult = ({ filtersConditions, shouldSearch, repoFilters, searchValue, selectedRepos, showResults, sortField, sortDirection }) => {
  const profiles = useSelector(selectAuthProfiles);
  const PAGE_SIZE = 10;
  const [documents, setDocuments] = useState([]);
  const [totalHits, setTotalHits] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);

  const repos = useMemo(() => {
    if (!selectedRepos?.length) return [];
    const repoList = selectedRepos.map((p) => p.value);
    return repoList;
  }, [selectedRepos]);

  // Handle scroll to top when manually changing pages
  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
    window.scrollTo(0, 0);
    fetchPage(selected);
  };

  const fetchPage = useCallback(
    async (pageIndex = 0) => {
      if ((!repoFilters && !searchValue) || !repos.length) {
        setDocuments([]);
        setTotalHits(0);
        setPageCount(0);
        setHasSearched(true);
        return;
      }

      setIsLoading(true);
      setHasSearched(true);
      const firstResult = pageIndex * PAGE_SIZE + 1;

      try {
        const searchCriteria = buildSearchCriteria({
          searchValue,
          repoFilters,
          filtersConditions,
        });
        const sortBy = sortField?.map(({ field, direction }) => `${field}${direction === "ASC" ? "+" : "-"}`).join(",");
        const res = await advanceMultiSearch(repos, searchCriteria, sortBy ? sortBy : null, null, true, firstResult, PAGE_SIZE);
        const docs = res?.documents || [];
        const total = res?.totalHits || 0;

        setDocuments(docs);
        setTotalHits(total);
        setPageCount(Math.ceil(total / PAGE_SIZE) || 0);
      } catch (error) {
        console.error('Failed to fetch search results', error);
        setDocuments([]);
        setTotalHits(0);
        setPageCount(0);
      } finally {
        setIsLoading(false);
      }
    },
    [PAGE_SIZE, repos, searchValue, repoFilters, filtersConditions, sortField, sortDirection]
  );

  useEffect(() => {
    if (pageCount && currentPage > pageCount - 1) {
      setCurrentPage(pageCount - 1);
    }
  }, [pageCount, currentPage]);

  const whichProfile = (type) => {
    let primary = null;
    let secondaries = null;
    if (type === 'article') {
      primary = profiles.news;
      secondaries = [profiles.mitre, profiles.cve, profiles.podcasts];
    } else if (type === 'episode') {
      primary = profiles.podcasts;
      secondaries = [profiles.mitre, profiles.cve, profiles.news];
    } else if (mitreDocTypes.indexOf(type) > -1) {
      primary = profiles.mitre;
      secondaries = [profiles.podcasts, profiles.cve, profiles.news];
    } else if (type === 'vulnerability') {
      primary = profiles.cve;
      secondaries = [profiles.podcasts, profiles.mitre, profiles.news];
    }
    return {
      primary,
      secondaries,
    };
  };

  useEffect(() => {
    // Only trigger search when component is rendered AND has valid criteria
    const hasValidSearchCriteria =
      (shouldSearch && selectedRepos?.length > 0) &&
      (searchValue?.trim() || (repoFilters && Object.keys(repoFilters).length > 0));

    if (hasValidSearchCriteria) {
      setHasSearched(false);
      setCurrentPage(0);
      fetchPage(0);
    }
  }, [repoFilters, searchValue, selectedRepos, fetchPage, shouldSearch]);

  const showingStart =
    documents.length && totalHits
      ? currentPage * PAGE_SIZE + 1
      : 0;
  const showingEnd = documents.length
    ? Math.min(totalHits, currentPage * PAGE_SIZE + documents.length)
    : 0;

  return (
    <>
      <Row className='g-0'>
        <Col lg={12}>
          <div>
            {documents.length && showResults ? (
              <div>
                <span>
                  Showing {showingStart} - {showingEnd} of{' '}
                </span>
                <span>
                  <strong>{totalHits}</strong> Results
                </span>
              </div>
            ) : null}
          </div>
        </Col>
      </Row>
      <Row
        className='g-0'
      >
        <Col lg={12}>
          {!isLoading && documents.length && showResults ? (
            <CardGroup className='ctx-card-list'>
              {documents.map((doc) => {
                const profileInfo = whichProfile(doc.objectType);
                return (
                  <DocCard
                    key={doc.id}
                    doc={doc}
                    profilePrimary={profileInfo.primary}
                    profileSecondary={profileInfo.secondaries}
                    showAddWatchList={false}
                    showRemove={false}
                    list="advance-search"
                    allTags={[]}
                    selectedTags={[]}
                    cveFields={[]}
                    activeGroupKey={doc.objectType === 'vulnerability' ? 'cve' : doc.objectType === 'article' ? 'news' : 'general'}
                    cyberReliabilityLabels={null}
                    showPill={true}
                    hidePlaceholderImage={false}
                    showCuratorAction={true}
                  />
                );
              })}
            </CardGroup>
          ) : null}

          {!showResults || (!hasSearched && !shouldSearch && !isLoading && !documents.length) ? (
            <AdvanceSearchPageEmptyState />
          ) : null}

          {isLoading ? <DocListSkeleton />
            : null}
        </Col>
      </Row>
      <Row className='g-0'>
        <Col lg={12} className='mt-4'>
          {hasSearched && !isLoading && documents.length === 0 && showResults ? (
            <AlertMessage variant='danger' message='No results found for your search criteria. Try adjusting your filters or search terms.' />
          ) : null}
        </Col>
      </Row>
      <Row className='g-0'>
        <Col lg={12}>
          {pageCount > 1 && showResults ? (
            <ReactPaginate
              breakLabel='...'
              nextLabel='Next'
              onPageChange={handlePageChange}
              pageRangeDisplayed={3}
              marginPagesDisplayed={1}
              pageCount={pageCount}
              previousLabel='Previous'
              forcePage={currentPage}
              containerClassName='pagination justify-content-center ctx-pagination'
              pageClassName='page-item'
              pageLinkClassName='page-link'
              previousClassName='page-item'
              previousLinkClassName='page-link'
              nextClassName='page-item'
              nextLinkClassName='page-link'
              activeClassName='active'
              disabledClassName='disabled'
            />
          ) : null}
        </Col>
      </Row>
    </>
  );
};
export default AdvanceSearchResult;
